import { RouterProvider, createRouter } from "@tanstack/react-router";
import type { ApolloClient } from "@apollo/client";
import { routeTree } from "./routeTree.gen";
import { ShellHostProvider } from "./shell-handle/ShellHostProvider";
import type { ShellHandleHost } from "./shell-handle/createShellHandle";
import type { ConnectivityApi } from "./shell-handle/connectivity";
import { ToastNotificationsProvider } from "@/shared/coralreef";
import { ToastBridge } from "./presentational/ToastBridge";
import { ErrorBoundary } from "./presentational/ErrorBoundary";
import { ShellBootError } from "./presentational/ShellBootError";
import type { BootStatus } from "./shell-handle/bootController";
import { useNotificationSound } from "@/shared/hooks/useNotificationSound";

const router = createRouter({
  routeTree,
  defaultNotFoundComponent: () => null,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export interface AppProps {
  host: ShellHandleHost;
  client: ApolloClient<unknown>;
  cloudClient?: ApolloClient<unknown>;
  boot?: BootStatus;
  connectivity?: ConnectivityApi;
}

// The shell root is the only component mounted on every route, so it owns the
// alarm-sound listener. No MFE may mount it as well.
function NotificationSound() {
  useNotificationSound();
  return null;
}

export function App({
  host,
  client,
  cloudClient,
  boot,
  connectivity,
}: AppProps) {
  return (
    <ErrorBoundary>
      <ShellHostProvider
        host={host}
        client={client}
        cloudClient={cloudClient}
        connectivity={connectivity}
      >
        <NotificationSound />
        <ToastNotificationsProvider autoDismiss placement="bottom-left">
          <RouterProvider router={router} />
          <ToastBridge />
          {boot && <ShellBootError boot={boot} />}
        </ToastNotificationsProvider>
      </ShellHostProvider>
    </ErrorBoundary>
  );
}
